import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone, Star } from 'lucide-react';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Table, Tag, TextArea } from '@voice/ui';
import { errorText } from '../components/common';
import { api } from '../lib/api';
import { useSoftphone } from '../lib/softphone';
import styles from './Pages.module.css';

interface Contact {
  id: string;
  ownerUserId: string | null;
  name: string;
  company: string;
  email: string;
  notes: string;
  tags: string[];
  vip: boolean;
  blocked: boolean;
  numbers: { id: string; label: string; number: string }[];
}

const KEY = ['contacts'] as const;

export function Contacts() {
  const [search, setSearch] = useState('');
  const query = useQuery({ queryKey: [...KEY, search], queryFn: () => api<{ contacts: Contact[]; canShare: boolean }>(`/contacts${search ? `?search=${encodeURIComponent(search)}` : ''}`), placeholderData: (prev) => prev });
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showPhonebook, setShowPhonebook] = useState(false);
  const phone = useSoftphone();
  const canShare = query.data?.canShare ?? false;

  return (
    <div className={styles.page}>
      <PageHeader title="Kontakte">
        <Button variant="secondary" onClick={() => setShowPhonebook(true)}>
          Fürs Telefon
        </Button>
        <Button variant="secondary" onClick={() => setShowImport(true)}>
          Import / Export
        </Button>
        <Button onClick={() => setEditing('new')}>Neuer Kontakt</Button>
      </PageHeader>

      <Field label="Suchen" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, Firma oder Nummer" style={{ maxWidth: 420, marginBottom: 20 }} />

      {query.isError && <Notice tone="error">Die Kontakte konnten nicht geladen werden.</Notice>}
      {query.data && query.data.contacts.length === 0 && <EmptyState>{search ? 'Nichts gefunden.' : 'Noch keine Kontakte. Lege den ersten an oder importiere eine CSV-Datei.'}</EmptyState>}
      {query.data && query.data.contacts.length > 0 && (
        <Table aria-label="Kontakte">
          <thead>
            <tr>
              <th>Name</th>
              <th>Rufnummern</th>
              <th>Art</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {query.data.contacts.map((c) => (
              <tr key={c.id}>
                <th scope="row">
                  {c.vip && <Star size={14} aria-label="VIP" style={{ marginRight: 6, color: 'var(--accent-text)' }} />}
                  {c.name}
                  {c.company && <div className={styles.muted}>{c.company}</div>}
                </th>
                <td>
                  {c.numbers.map((n) => (
                    <div key={n.id} className={styles.inline}>
                      <span className={styles.mono}>{n.number}</span>
                      <span className={styles.muted}>{n.label}</span>
                      {phone.status === 'ready' && (
                        <button type="button" className={styles.iconBtn} onClick={() => void phone.dial(n.number)} aria-label={`${c.name} unter ${n.number} anrufen`}>
                          <Phone size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </td>
                <td>
                  <span className={styles.inline}>
                    {c.ownerUserId === null ? <Tag>Firma</Tag> : <Tag tone="neutral">Persönlich</Tag>}
                    {c.blocked && <Tag tone="busy">Gesperrt</Tag>}
                  </span>
                </td>
                <td>
                  <RowActions>
                    <Button variant="secondary" onClick={() => setEditing(c)} disabled={c.ownerUserId === null && !canShare}>
                      Bearbeiten
                    </Button>
                  </RowActions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing && <ContactForm key={editing === 'new' ? 'new' : editing.id} contact={editing === 'new' ? null : editing} canShare={canShare} onClose={() => setEditing(null)} />}
      {showImport && <ImportExport canShare={canShare} onClose={() => setShowImport(false)} />}
      {showPhonebook && <PhonebookInfo onClose={() => setShowPhonebook(false)} />}
    </div>
  );
}

function ContactForm({ contact, canShare, onClose }: { contact: Contact | null; canShare: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: contact?.name ?? '',
    company: contact?.company ?? '',
    email: contact?.email ?? '',
    notes: contact?.notes ?? '',
    tags: (contact?.tags ?? []).join(', '),
    vip: contact?.vip ?? false,
    blocked: contact?.blocked ?? false,
    shared: contact ? contact.ownerUserId === null : false,
    numbers: contact?.numbers.map((n) => ({ label: n.label, number: n.number })) ?? [{ label: 'Telefon', number: '' }],
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));
  const body = () => ({ ...f, tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean), numbers: f.numbers.filter((n) => n.number.trim()) });
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const save = useMutation({
    mutationFn: () => (contact ? api(`/contacts/${contact.id}`, { method: 'PUT', body: body() }) : api('/contacts', { method: 'POST', body: body() })),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/contacts/${contact!.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={contact ? contact.name : 'Neuer Kontakt'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!f.name || !f.numbers.some((n) => n.number.trim()) || save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div className={styles.row}>
          <Field label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus />
          <Field label="Firma" value={f.company} onChange={(e) => set('company', e.target.value)} />
          <Field label="E-Mail" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
          <Field label="Schlagwörter (Komma)" value={f.tags} onChange={(e) => set('tags', e.target.value)} />
        </div>
        <div>
          <h3 className={styles.heading}>Rufnummern</h3>
          <div className={styles.stack}>
            {f.numbers.map((n, i) => (
              <div key={i} className={styles.inline} style={{ alignItems: 'flex-end' }}>
                <Field label="Art" value={n.label} onChange={(e) => set('numbers', f.numbers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} style={{ width: 140 }} />
                <Field label="Nummer" value={n.number} onChange={(e) => set('numbers', f.numbers.map((x, j) => (j === i ? { ...x, number: e.target.value } : x)))} placeholder="+49 371 123456" style={{ flex: 1, minWidth: 180 }} />
                {f.numbers.length > 1 && (
                  <Button variant="secondary" onClick={() => set('numbers', f.numbers.filter((_, j) => j !== i))}>
                    Entfernen
                  </Button>
                )}
              </div>
            ))}
            <div>
              <Button variant="secondary" onClick={() => set('numbers', [...f.numbers, { label: 'Mobil', number: '' }])}>
                Weitere Nummer
              </Button>
            </div>
          </div>
        </div>
        <TextArea label="Notizen" value={f.notes} onChange={(e) => set('notes', e.target.value)} rows={3} />
        <Checkbox label="VIP" hint="Wird bei eingehenden Anrufen hervorgehoben und kann in Abläufen bevorzugt werden." checked={f.vip} onChange={(e) => set('vip', e.target.checked)} />
        <Checkbox label="Sperren" hint="Anrufe dieser Nummern werden abgewiesen." checked={f.blocked} onChange={(e) => set('blocked', e.target.checked)} />
        {canShare && <Checkbox label="Firmenkontakt (für alle sichtbar)" checked={f.shared} onChange={(e) => set('shared', e.target.checked)} disabled={contact !== null} />}
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}
        {contact && (
          <div>
            <Button variant="secondary" onClick={() => confirm(`„${contact.name}“ löschen?`) && remove.mutate()}>
              Kontakt löschen
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ImportExport({ canShare, onClose }: { canShare: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [shared, setShared] = useState(false);
  const run = useMutation({
    mutationFn: async () => api<{ imported: number; skipped: number }>('/contacts/import', { method: 'POST', body: { csv: await file!.text(), shared } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  return (
    <Modal open onClose={onClose} title="Import und Export" size="md" footer={<Button onClick={onClose}>Schließen</Button>}>
      <div className={styles.form}>
        <div>
          <h3 className={styles.heading}>Exportieren</h3>
          <a href="/api/contacts/export.csv" download>
            Alle sichtbaren Kontakte als CSV herunterladen
          </a>
        </div>
        <div>
          <h3 className={styles.heading}>Importieren</h3>
          <p className={styles.note} style={{ marginBottom: 12 }}>
            CSV mit Kopfzeile, z. B. aus Outlook, Google Kontakte oder Excel. Erkannt werden die Spalten Name (oder Vorname/Nachname), Firma, E-Mail, Notizen sowie Telefon, Mobil, Festnetz …
          </p>
          <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label="CSV-Datei" />
          {canShare && (
            <div style={{ marginTop: 12 }}>
              <Checkbox label="Als Firmenkontakte anlegen (für alle sichtbar)" checked={shared} onChange={(e) => setShared(e.target.checked)} />
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <Button onClick={() => run.mutate()} disabled={!file || run.isPending}>
              Importieren
            </Button>
          </div>
          {run.isError && <Notice tone="error">{errorText(run.error)}</Notice>}
          {run.data && (
            <Notice tone="success">
              {run.data.imported} importiert{run.data.skipped > 0 ? `, ${run.data.skipped} übersprungen (Name oder Nummer fehlte)` : ''}.
            </Notice>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function PhonebookInfo({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['phonebook'], queryFn: () => api<{ token: string; urls: { json: string; csv: string; xml: string } }>('/me/phonebook') });
  const rotate = useMutation({ mutationFn: () => api('/me/phonebook/rotate', { method: 'POST' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['phonebook'] }) });
  return (
    <Modal open onClose={onClose} title="Kontakte auf dem Telefon" description="Tischtelefon und Softphone können dieses Verzeichnis per Adresse laden." size="md" footer={<Button onClick={onClose}>Schließen</Button>}>
      <div className={styles.form}>
        {query.data && (
          <dl className={styles.credentials}>
            <dt>Yealink / Grandstream (XML)</dt>
            <dd>{query.data.urls.xml}</dd>
            <dt>Allgemein (CSV)</dt>
            <dd>{query.data.urls.csv}</dd>
            <dt>JSON</dt>
            <dd>{query.data.urls.json}</dd>
          </dl>
        )}
        <p className={styles.note}>Das Verzeichnis enthält alle internen Nebenstellen, die Firmenkontakte und deine persönlichen Kontakte. Die Adresse ist geheim – wer sie kennt, kann das Verzeichnis lesen.</p>
        <div>
          <Button variant="secondary" onClick={() => confirm('Neue Adresse erzeugen? Die alte funktioniert dann nicht mehr.') && rotate.mutate()}>
            Neue Adresse erzeugen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
